import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Building2, Phone, MapPin, Mail, Globe, Instagram, Linkedin, Link2, Shield, Key, Download, AlertTriangle } from 'lucide-react';
import api, { authAPI } from '../services/api';

const ProfilePage = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    dealer_name: '',
    company_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    website_url: '',
    instagram_handle: '',
    linkedin_url: '',
    tagline: '',
    bio: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Security: MFA, export, delete
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [showMfaDisableModal, setShowMfaDisableModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [securityMessage, setSecurityMessage] = useState({ type: '', text: '' });
  const [securityLoading, setSecurityLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        dealer_name: user.dealer_name || '',
        company_name: user.company_name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || '',
        postal_code: user.postal_code || '',
        website_url: user.website_url || '',
        instagram_handle: user.instagram_handle || '',
        linkedin_url: user.linkedin_url || '',
        tagline: user.tagline || '',
        bio: user.bio || '',
      });
    }
    fetchMfaStatus();
  }, [user]);

  const fetchMfaStatus = async () => {
    if (!user) return;
    try {
      const res = await authAPI.mfaStatus();
      setMfaEnabled(res.data?.mfa_enabled ?? false);
    } catch {
      setMfaEnabled(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put('/auth/me', formData);
      updateUser(response.data);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to update profile'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMfaSetup = async () => {
    setSecurityMessage({ type: '', text: '' });
    setSecurityLoading(true);
    try {
      const res = await authAPI.mfaSetup();
      setMfaSetupData({ secret: res.data.secret, provisioning_uri: res.data.provisioning_uri });
    } catch (e) {
      setSecurityMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to start MFA setup' });
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e?.preventDefault();
    if (!mfaVerifyCode || mfaVerifyCode.length !== 6) {
      setSecurityMessage({ type: 'error', text: 'Enter the 6-digit code from your app' });
      return;
    }
    setSecurityLoading(true);
    setSecurityMessage({ type: '', text: '' });
    try {
      await authAPI.mfaVerify(mfaVerifyCode);
      setMfaEnabled(true);
      setMfaSetupData(null);
      setMfaVerifyCode('');
      setSecurityMessage({ type: 'success', text: 'MFA enabled successfully.' });
      const profile = await authAPI.getCurrentUser();
      updateUser(profile.data);
    } catch (e) {
      setSecurityMessage({ type: 'error', text: e.response?.data?.detail || 'Invalid code. Try again.' });
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleMfaDisable = async (e) => {
    e?.preventDefault();
    if (!mfaDisablePassword || !mfaDisableCode || mfaDisableCode.length !== 6) {
      setSecurityMessage({ type: 'error', text: 'Enter your password and 6-digit code' });
      return;
    }
    setSecurityLoading(true);
    setSecurityMessage({ type: '', text: '' });
    try {
      await authAPI.mfaDisable(mfaDisablePassword, mfaDisableCode);
      setMfaEnabled(false);
      setShowMfaDisableModal(false);
      setMfaDisablePassword('');
      setMfaDisableCode('');
      logout();
      navigate('/login');
    } catch (e) {
      setSecurityMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to disable MFA' });
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleExportData = async () => {
    setSecurityLoading(true);
    setSecurityMessage({ type: '', text: '' });
    try {
      const res = await authAPI.exportMyData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `my-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      setSecurityMessage({ type: 'success', text: 'Data exported. File downloaded.' });
    } catch (e) {
      setSecurityMessage({ type: 'error', text: e.response?.data?.detail || 'Export failed' });
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e?.preventDefault();
    if (!deletePassword) {
      setSecurityMessage({ type: 'error', text: 'Enter your password to confirm' });
      return;
    }
    setSecurityLoading(true);
    setSecurityMessage({ type: '', text: '' });
    try {
      await authAPI.deleteAccount(deletePassword);
      logout();
      navigate('/');
    } catch (e) {
      setSecurityMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to delete account' });
    } finally {
      setSecurityLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Profile Settings</h1>
        <p className="text-slate-600">Manage your account information</p>
      </div>

      {/* Account Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">{user?.dealer_name}</h2>
            <p className="text-slate-600 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {user?.email}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
          <div>
            <p className="text-sm text-slate-500">Member since</p>
            <p className="font-medium text-slate-800">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Last login</p>
            <p className="font-medium text-slate-800">
              {user?.last_login ? new Date(user.last_login).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Edit Profile Form */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Edit Profile</h3>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div>
            <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Dealer Name *
                </label>
                <input
                  type="text"
                  name="dealer_name"
                  value={formData.dealer_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div>
            <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Business Information
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tagline
                </label>
                <input
                  type="text"
                  name="tagline"
                  placeholder="e.g. Authorized Rolex dealer · Buy, sell, trade"
                  value={formData.tagline}
                  onChange={handleChange}
                  maxLength={200}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-1">Short one-liner on your profile</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  About / Bio
                </label>
                <textarea
                  name="bio"
                  placeholder="A few sentences about you and your business..."
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  maxLength={2000}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-1">Shown on your public and network profile</p>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Address
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Social & public profile */}
          <div>
            <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Social & public profile
            </h4>
            <p className="text-sm text-slate-600 mb-4">
              These links appear on your public dealer profile so clients can find you. Your public profile is shareable (e.g. for social media or business cards).
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  name="website_url"
                  placeholder="https://yoursite.com"
                  value={formData.website_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Instagram handle
                </label>
                <input
                  type="text"
                  name="instagram_handle"
                  placeholder="@yourhandle or yourhandle"
                  value={formData.instagram_handle}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  name="linkedin_url"
                  placeholder="https://linkedin.com/in/yourprofile"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              {user?.id && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Your public profile
                  </p>
                  <a
                    href={`${window.location.origin}/dealer/${user.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:underline break-all"
                  >
                    {window.location.origin}/dealer/{user.id}
                  </a>
                  <p className="text-xs text-slate-500 mt-1">
                    Share this link so clients can see your public profile and listings.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Security Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Security
        </h3>
        <p className="text-sm text-slate-600 mb-6">
          Multi-factor authentication, data export, and account deletion.
        </p>

        {securityMessage.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            securityMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {securityMessage.text}
          </div>
        )}

        {/* MFA */}
        <div className="mb-8">
          <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Key className="w-4 h-4" />
            Two-factor authentication (MFA)
          </h4>
          {user?.is_admin && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              Administrators must have MFA enabled to sign in.
            </p>
          )}
          {!mfaSetupData ? (
            <>
              <p className="text-sm text-slate-600 mb-3">
                {mfaEnabled ? 'MFA is enabled. Use your authenticator app when signing in.' : 'Add an extra layer of security with a 6-digit code from an app like Google Authenticator or Authy.'}
              </p>
              {mfaEnabled ? (
                <button
                  type="button"
                  onClick={() => setShowMfaDisableModal(true)}
                  disabled={securityLoading}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  Disable MFA
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleMfaSetup}
                  disabled={securityLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {securityLoading ? 'Starting...' : 'Enable MFA'}
                </button>
              )}
            </>
          ) : (
            <div className="border border-slate-200 rounded-lg p-4 space-y-4">
              <p className="text-sm text-slate-700">Scan this QR code with your authenticator app, or enter the secret manually:</p>
              <div className="flex flex-wrap gap-6 items-start">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(mfaSetupData.provisioning_uri)}`}
                  alt="MFA QR code"
                  className="rounded border border-slate-200"
                />
                <div className="text-sm">
                  <p className="text-slate-500 mb-1">Manual entry secret:</p>
                  <code className="block bg-slate-100 p-2 rounded break-all text-xs">{mfaSetupData.secret}</code>
                </div>
              </div>
              <form onSubmit={handleMfaVerify} className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Enter 6-digit code from app</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={mfaVerifyCode}
                    onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <button type="submit" disabled={securityLoading || mfaVerifyCode.length !== 6} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {securityLoading ? 'Verifying...' : 'Enable MFA'}
                </button>
                <button type="button" onClick={() => { setMfaSetupData(null); setMfaVerifyCode(''); setSecurityMessage({ type: '', text: '' }); }} className="text-slate-600 hover:text-slate-800 text-sm">
                  Cancel
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Export data */}
        <div className="mb-8">
          <h4 className="font-medium text-slate-700 mb-2">Export your data</h4>
          <p className="text-sm text-slate-600 mb-3">
            Download a copy of your profile and data counts (GDPR/CCPA data portability).
          </p>
          <button type="button" onClick={handleExportData} disabled={securityLoading} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" />
            {securityLoading ? 'Exporting...' : 'Export my data'}
          </button>
        </div>

        {/* Delete account */}
        <div>
          <h4 className="font-medium text-slate-700 mb-2 text-red-700">Delete account</h4>
          <p className="text-sm text-slate-600 mb-3">
            Permanently anonymize and deactivate your account. This cannot be undone.
          </p>
          <button type="button" onClick={() => setShowDeleteModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors">
            <AlertTriangle className="w-4 h-4" />
            Delete my account
          </button>
        </div>
      </div>

      {/* MFA Disable modal */}
      {showMfaDisableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Disable MFA</h3>
            <p className="text-sm text-slate-600 mb-4">Enter your password and current 6-digit code. You will be signed out.</p>
            <form onSubmit={handleMfaDisable} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input type="password" value={mfaDisablePassword} onChange={(e) => setMfaDisablePassword(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current 6-digit code</label>
                <input type="text" inputMode="numeric" value={mfaDisableCode} onChange={(e) => setMfaDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="000000" maxLength={6} required />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowMfaDisableModal(false); setMfaDisablePassword(''); setMfaDisableCode(''); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button type="submit" disabled={securityLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Disable MFA</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete account modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-red-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete account
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              This will permanently anonymize your account and sign you out. Your data will be removed in line with our privacy policy. This cannot be undone.
            </p>
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Enter your password to confirm</label>
                <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" required />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowDeleteModal(false); setDeletePassword(''); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button type="submit" disabled={securityLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Delete my account</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProfilePage;
